import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Card,
  CardContent,
  Button,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import appRegistryService from "../services/api/appRegistryService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSearchTerm,
  setEnvFilter,
  setStatusFilter,
} from "../redux/slices/clientsSlice";
import { fetchClients } from "../redux/thunks/clientsThunks";
import SyncBadge from "../components/SyncBadge";

const ENV_COLORS = {
  prod: "error",
  production: "error",
  staging: "warning",
  dev: "info",
  development: "info",
};

const ClientDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    clients,
    searchTerm,
    envFilter,
    statusFilter,
    loading,
    error,
  } = useSelector((state) => state.clients);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const [snackbar, setSnackbar] = useState(null);
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    if (refreshCooldown <= 0) return undefined;
    const t = setTimeout(() => setRefreshCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [refreshCooldown]);

  const triggerRefreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await appRegistryService.refreshNow();
      if (res.status === 202) {
        setSnackbar({
          severity: "info",
          msg: `Refreshing… (background job ${res.data.jobId} started, poll for status)`,
        });
        setRefreshCooldown(res.data.debounceWindow || 15);
      } else if (res.status === 429) {
        const secs = Math.ceil((res.data.retryAfterMs || 15000) / 1000);
        setSnackbar({ severity: "warning", msg: `Refresh debounced — try again in ${secs}s` });
        setRefreshCooldown(secs);
      }
    } catch (e) {
      setSnackbar({ severity: "error", msg: `Refresh failed: ${e.message}` });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  const filteredClients = useMemo(() => {
    let filtered = [...clients];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          (client.name && client.name.toLowerCase().includes(term)) ||
          (client.tag && client.tag.toLowerCase().includes(term)) ||
          (client.displayName && client.displayName.toLowerCase().includes(term))
      );
    }

    if (envFilter !== "all") {
      filtered = filtered.filter(
        (client) => (client.environment || "").toLowerCase() === envFilter
      );
    }

    if (statusFilter !== "all") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter(
        (client) => client.isActive === isActive || client.status === statusFilter
      );
    }

    if (sortBy === "lastSyncOldest") {
      // Null / never-synced first, then oldest lastSyncAt ascending.
      filtered.sort((a, b) => {
        const at = a.lastSyncAt ? new Date(a.lastSyncAt).getTime() : 0;
        const bt = b.lastSyncAt ? new Date(b.lastSyncAt).getTime() : 0;
        return at - bt;
      });
    } else if (sortBy === "lastSyncNewest") {
      filtered.sort((a, b) => {
        const at = a.lastSyncAt ? new Date(a.lastSyncAt).getTime() : 0;
        const bt = b.lastSyncAt ? new Date(b.lastSyncAt).getTime() : 0;
        return bt - at;
      });
    } else {
      filtered.sort((a, b) =>
        (a.displayName || a.name || "").localeCompare(b.displayName || b.name || "")
      );
    }

    return filtered;
  }, [clients, searchTerm, envFilter, statusFilter, sortBy]);

  const envCounts = useMemo(() => {
    const counts = { prod: 0, staging: 0, dev: 0 };
    clients.forEach((c) => {
      const env = (c.environment || "").toLowerCase();
      if (env === "prod" || env === "production") counts.prod++;
      else if (env === "staging") counts.staging++;
      else if (env === "dev" || env === "development") counts.dev++;
    });
    return counts;
  }, [clients]);

  const activeCounts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    clients.forEach((c) => {
      if (c.isActive !== false && c.status !== "inactive") active++;
      else inactive++;
    });
    return { active, inactive };
  }, [clients]);

  if (loading && clients.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Typography color="error">Error: {error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <BusinessIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Client Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clients.length} clients registered
          </Typography>
        </Box>
        <Tooltip title="Enqueue a full catalog refresh across all clients (15s global debounce)">
          <span>
            <Button
              data-testid="refresh-all-now-btn"
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={triggerRefreshAll}
              disabled={refreshing || refreshCooldown > 0}
            >
              {refreshCooldown > 0
                ? `Wait ${refreshCooldown}s`
                : refreshing
                ? "Refreshing…"
                : "Refresh all (sync now)"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.msg}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Summary Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h4" color="primary">{clients.length}</Typography>
            <Typography variant="caption" color="text.secondary">Total Clients</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h4" color="success.main">{activeCounts.active}</Typography>
            <Typography variant="caption" color="text.secondary">Active</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h4" color="error.main">{envCounts.prod}</Typography>
            <Typography variant="caption" color="text.secondary">Production</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h4" color="warning.main">{envCounts.staging}</Typography>
            <Typography variant="caption" color="text.secondary">Staging</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h4" color="info.main">{envCounts.dev}</Typography>
            <Typography variant="caption" color="text.secondary">Dev</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={envFilter}
            label="Environment"
            onChange={(e) => dispatch(setEnvFilter(e.target.value))}
          >
            <MenuItem value="all">All Environments</MenuItem>
            <MenuItem value="prod">Production</MenuItem>
            <MenuItem value="staging">Staging</MenuItem>
            <MenuItem value="dev">Development</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => dispatch(setStatusFilter(e.target.value))}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            data-testid="clients-sort-by"
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="name">Name (A→Z)</MenuItem>
            <MenuItem value="lastSyncOldest">Last synced (oldest first)</MenuItem>
            <MenuItem value="lastSyncNewest">Last synced (newest first)</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Client Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell><strong>Client Name</strong></TableCell>
              <TableCell><strong>Tag</strong></TableCell>
              <TableCell><strong>Environment</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Sync</strong></TableCell>
              <TableCell><strong>Database</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => {
                const env = (client.environment || "unknown").toLowerCase();
                const isActive = client.isActive !== false && client.status !== "inactive";
                return (
                  <TableRow
                    key={client.id || client.tag}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/clients/${client.id || client.tag}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {client.displayName || client.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                        {client.tag}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={env}
                        size="small"
                        color={ENV_COLORS[env] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {isActive ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Inactive"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <SyncBadge
                        lastSyncAt={client.lastSyncAt}
                        metabaseDatabaseId={client.metabaseDatabaseId}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary">
                        {client.dbHost || client.databaseHost || "N/A"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No clients found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ClientDashboard;
